import mainRefs from "@/__main__/refs.mjs";
import exitTransitionsRefs from "@/util/exit-transitions.refs.mjs";
import globals from "@/util/global-whitelist.mjs";
import nextFrame from "@/util/next-frame.mjs";

// Why implement exit transitioning animation without using a library?
// We want to make sure that the exiting node is completely inert, it should
// not re-render or be interactive. Most libs will retain the exiting node
// and allow re-render, interaction, etc.
//
// Also this will let use fine-tune the performance, since this is the biggest
// animation.

export const OBSERVE_CLASS = "route-wrapper";
export const CONTAINER_ID = "main-content";
const EXIT_TIME = 350;

const transition = `transform ${EXIT_TIME / 1000}s var(--bezier), opacity ${
  EXIT_TIME / 1000
}s var(--bezier)`;

const observer =
  typeof MutationObserver !== "undefined" &&
  new MutationObserver((mutations) => {
    if (!exitTransitionsRefs.shouldTransition) return;

    const enterNodes: HTMLElement[] = [];
    const exitNodes: [HTMLElement, Node][] = [];
    for (const { addedNodes, removedNodes, target } of mutations) {
      for (const node of Array.from(removedNodes)) {
        if (!(node instanceof HTMLElement)) continue;
        if (!node.classList.contains(OBSERVE_CLASS)) continue;
        exitNodes.push([node, target]);
      }
      for (const node of Array.from(addedNodes)) {
        if (!(node instanceof HTMLElement)) continue;
        if (!node.classList.contains(OBSERVE_CLASS)) continue;
        enterNodes.push(node);
      }
    }

    const isBackwards = mainRefs.router?.isBackwards ?? false;

    for (const node of enterNodes) {
      node.style.opacity = "0";
      node.style.transform = `translate3d(calc(var(--sp-8) * ${
        isBackwards ? 1 : -1
      }), 0, 0)`;
      nextFrame(() => {
        node.style.transition = transition;
        node.style.opacity = "1";
        // REMOVE transform instead of setting to 0, this prevents preserving the new stacking context
        node.style.removeProperty("transform");
      });
    }

    if (exitNodes.length) {
      for (const [node, target] of exitNodes) {
        node.classList.remove(OBSERVE_CLASS);
        node.inert = true;
        target.appendChild(node);
        handleExitNode(node, isBackwards);
      }
    }
  });

// The reason why this is observing the document body, is because
// there may be transitions out of app routes, in which case the
// app container ID may not be present.
if (observer) {
  observer.observe(globals.document.body, {
    childList: true,
    subtree: true,
  });
}

function handleExitNode(node: HTMLElement, isBackwards: boolean) {
  // prevent inert node from affecting scroll max
  node.style.maxHeight = "100%";
  node.style.overflow = "hidden";

  node.style.pointerEvents = "none"; // enforce inert

  // hack to get the node's previous position.
  node.style.position = "relative";
  node.style.left = `${node.offsetLeft}px`;
  node.style.removeProperty("position");

  // since we are changing props on the first frame after moving the node,
  // we need to sync the next frame to animate
  nextFrame(() => {
    node.style.opacity = "0";
    node.style.transition = transition;
    node.style.transform = `translate3d(calc(var(--sp-8) * ${
      isBackwards ? -1 : 1
    }), 0, 0)`;
  });
  setTimeout(function removeNode() {
    node.remove();
  }, EXIT_TIME);
}
